import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from "react";
import FullscreenExitOutlinedIcon from "@mui/icons-material/FullscreenExitOutlined";
import FullscreenOutlinedIcon from "@mui/icons-material/FullscreenOutlined";
import CanvasStage from "./CanvasStage";
import CanvasTextEditorOverlay from "./CanvasTextEditorOverlay";
import NativeCanvasStyleBar from "./NativeCanvasStyleBar";
import NativeCanvasToolbar from "./NativeCanvasToolbar";
import CanvasAiPanel from "../../../features/canvasAi/components/CanvasAiPanel";
import { CANVAS_AI_DRAFT_DOCUMENT_KEY } from "../../../features/canvasAi/canvasAi.constants";
import { useCanvasAiAssistant } from "../../../features/canvasAi/useCanvasAiAssistant";
import { useCanvasEngine } from "./useCanvasEngine";
import { useCanvasImageSync } from "./useCanvasImageSync";
import "./CanvasWorkspace.css";

const CANVAS_CLASS =
  "relative h-[calc(100vh-220px)] min-h-[520px] overflow-hidden rounded-md border border-border bg-[var(--color-canvas-background)]";
const FULLSCREEN_CANVAS_CLASS =
  "fixed inset-0 z-50 overflow-hidden border-0 bg-[var(--color-canvas-background)]";
const FULLSCREEN_TOGGLE_CLASS =
  "absolute right-4 top-4 z-30 flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-surface/95 text-text shadow-2xl backdrop-blur transition-colors hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-primary";
const ENTER_FULLSCREEN_LABEL = "Enter full screen";
const EXIT_FULLSCREEN_LABEL = "Exit full screen";
const IMAGE_MIME_TYPES = "image/*";

const getSectionClassName = (isFullscreen) =>
  isFullscreen ? FULLSCREEN_CANVAS_CLASS : CANVAS_CLASS;

const FullscreenToggleButton = ({ isFullscreen, onToggle }) => {
  const label = isFullscreen ? EXIT_FULLSCREEN_LABEL : ENTER_FULLSCREEN_LABEL;
  const Icon = isFullscreen ? FullscreenExitOutlinedIcon : FullscreenOutlinedIcon;

  return (
    <button
      aria-label={label}
      className={FULLSCREEN_TOGGLE_CLASS}
      data-testid="netpad-canvas-fullscreen-toggle"
      onClick={onToggle}
      title={label}
      type="button"
    >
      <Icon className="h-5 w-5" fontSize="small" />
    </button>
  );
};

/**
 * Excalidraw-style diagram workspace. Scene state and rendering live in the
 * imperative canvas engine; React renders only the chrome around it. When
 * `isCloudSyncEnabled` is true, pasted/selected images upload to blob storage
 * in the background instead of persisting as base64. In fullscreen the section
 * becomes a fixed viewport overlay (CSS, not the browser Fullscreen API) and
 * Escape-on-idle exits via `onToggleFullscreen`. The forwarded ref exposes
 * `getContent()` so explicit saves read the live scene instead of the
 * debounced `onChange` state.
 * @param {{ documentKey?: string, isCloudSyncEnabled?: boolean, isFullscreen?: boolean, onChange: (content: string) => void, onImageSyncError?: (message: string) => void, onSaveRequest?: Function, onToggleFullscreen?: Function, value: string }} props
 * @returns {JSX.Element}
 */
const CanvasWorkspace = forwardRef(function CanvasWorkspace(
  {
    documentKey = CANVAS_AI_DRAFT_DOCUMENT_KEY,
    isCloudSyncEnabled = false,
    isFullscreen = false,
    onChange,
    onImageSyncError,
    onSaveRequest,
    onToggleFullscreen,
    value,
  },
  ref
) {
  const [isAiPanelOpen, setIsAiPanelOpen] = useState(false);
  const canExitOnEscape = isFullscreen && Boolean(onToggleFullscreen);
  const handleEscapeIdle = useCallback(() => {
    if (canExitOnEscape) onToggleFullscreen();
  }, [canExitOnEscape, onToggleFullscreen]);
  const canvas = useCanvasEngine({
    onChange,
    onEscapeIdle: canExitOnEscape ? handleEscapeIdle : undefined,
    onSaveShortcut: onSaveRequest,
    value,
  });
  const engine = canvas.engine;
  const assistant = useCanvasAiAssistant({ documentKey, engine });
  const imageSync = useCanvasImageSync({
    engine,
    isEnabled: isCloudSyncEnabled,
    onSyncError: onImageSyncError,
    value,
  });
  useImperativeHandle(
    ref,
    () => ({
      getContent: canvas.getContent,
      waitForImageSync: imageSync.waitForImageSync,
    }),
    [canvas.getContent, imageSync.waitForImageSync]
  );

  return (
    <section
      aria-label="Canvas workspace"
      className={getSectionClassName(isFullscreen)}
    >
      <CanvasStage engine={engine} />
      <NativeCanvasToolbar
        canRedo={canvas.canRedo}
        canUndo={canvas.canUndo}
        isAiPanelOpen={isAiPanelOpen}
        isToolLocked={canvas.isToolLocked}
        onAiPanelToggle={() => setIsAiPanelOpen((previous) => !previous)}
        onRedo={engine.redo}
        onToggleToolLock={engine.toggleToolLock}
        onToolChange={canvas.handleToolChange}
        onUndo={engine.undo}
        onZoomIn={() => engine.zoomBy({ direction: 1 })}
        onZoomOut={() => engine.zoomBy({ direction: -1 })}
        selectedTool={canvas.selectedTool}
        zoomLabel={canvas.zoomLabel}
      />
      {isAiPanelOpen && (
        <CanvasAiPanel
          assistant={assistant}
          onClose={() => setIsAiPanelOpen(false)}
        />
      )}
      {onToggleFullscreen && (
        <FullscreenToggleButton
          isFullscreen={isFullscreen}
          onToggle={onToggleFullscreen}
        />
      )}
      <CanvasTextEditorOverlay
        editingElementId={canvas.editingElementId}
        editingValue={canvas.editingValue}
        engine={engine}
        onCancel={engine.cancelTextEditing}
        onChange={engine.updateEditingValue}
        onCommit={engine.commitTextEditing}
      />
      <NativeCanvasStyleBar
        onDelete={engine.deleteSelected}
        onDuplicate={engine.duplicateSelected}
        onStyleChange={engine.applyStyle}
        selectedCount={canvas.selectedCount}
        style={canvas.selectedStyle}
      />
      <input
        accept={IMAGE_MIME_TYPES}
        className="hidden"
        onChange={canvas.handleImageSelect}
        ref={canvas.imageInputRef}
        type="file"
      />
    </section>
  );
});

export default CanvasWorkspace;
